import { useEffect, useRef, useState, type DependencyList } from 'react';

// Debug logging for SSE streams. Enable at runtime by setting
// localStorage.setItem('sseDebug', 'true') and refreshing. Logs are keyed by
// the caller's logPrefix (e.g. [dgpt-sse], [events-sse]).
export const isSseDebugEnabled = (): boolean => {
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage?.getItem('sseDebug') === 'true'
    );
  } catch {
    return false;
  }
};

export type UseSseStreamOptions<T> = {
  url: string;
  onEvent: (payload: T) => void;
  getToken: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
  getIsTeams: () => boolean;
  logPrefix: string;
  maxReconnectAttempts?: number;
  reconnectDelayMs?: (attempt: number) => number;
  reconnectLogLevel?: 'warn' | 'log';
  deps?: DependencyList;
};

export const useSseStream = <T = Record<string, unknown>>({
  url,
  onEvent,
  getToken,
  getIsTeams,
  logPrefix,
  maxReconnectAttempts = 4,
  reconnectDelayMs = (attempt) => attempt * 3000,
  reconnectLogLevel = 'warn',
  deps = [],
}: UseSseStreamOptions<T>) => {
  const [isConnected, setIsConnected] = useState(false);

  const onEventRef = useRef(onEvent);
  const getTokenRef = useRef(getToken);
  const getIsTeamsRef = useRef(getIsTeams);

  useEffect(() => {
    onEventRef.current = onEvent;
    getTokenRef.current = getToken;
    getIsTeamsRef.current = getIsTeams;
  });

  useEffect(() => {
    let abortController = new AbortController();
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let isCleaningUp = false;

    const logReconnect = (message: string) => {
      if (reconnectLogLevel === 'warn') {
        console.warn(message);
      } else {
        console.log(message);
      }
    };

    const logMaxAttempts = (message: string) => {
      if (reconnectLogLevel === 'warn') {
        console.error(message);
      } else {
        console.log(message);
      }
    };

    function scheduleReconnect() {
      if (reconnectAttempts >= maxReconnectAttempts) {
        logMaxAttempts(`${logPrefix} Max reconnection attempts reached`);
        return;
      }

      reconnectAttempts++;
      const delay = reconnectDelayMs(reconnectAttempts);
      logReconnect(
        `${logPrefix} Reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts}/${maxReconnectAttempts})...`
      );

      reconnectTimeout = setTimeout(() => {
        if (isCleaningUp) return;
        abortController = new AbortController();
        connectSSE();
      }, delay);
    }

    async function connectSSE() {
      if (isCleaningUp) return;

      let token: string | null | undefined;
      let isTeams: boolean;
      try {
        token = await getTokenRef.current();
        isTeams = getIsTeamsRef.current();
      } catch (err) {
        if (isCleaningUp) return;
        setIsConnected(false);
        console.error(`${logPrefix} Failed to get access token:`, err);
        scheduleReconnect();
        return;
      }

      if (!token) {
        setIsConnected(false);
        logReconnect(`${logPrefix} No access token available`);
        scheduleReconnect();
        return;
      }

      try {
        console.log(`${logPrefix} Connecting...`);
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-teamsapp': isTeams ? 'true' : 'false',
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        if (!response.body) {
          throw new Error('Response body is null');
        }

        console.log(`${logPrefix} Connected`);
        if (isSseDebugEnabled()) {
          console.log(`${logPrefix} [debug] stream open, awaiting frames`);
        }
        setIsConnected(true);
        reconnectAttempts = 0;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log(`${logPrefix} Stream closed by server`);
            setIsConnected(false);
            scheduleReconnect();
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            if (!event.trim()) continue;
            if (event.trim().startsWith(':')) {
              if (isSseDebugEnabled()) {
                console.log(`${logPrefix} [debug] heartbeat`);
              }
              continue;
            }

            const lines = event.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonString = line.slice(6).trim();
                  const payload = JSON.parse(jsonString) as T;
                  if (isSseDebugEnabled()) {
                    const keys =
                      payload && typeof payload === 'object'
                        ? Object.keys(payload as Record<string, unknown>)
                        : [typeof payload];
                    console.log(
                      `${logPrefix} [debug] frame received keys=${JSON.stringify(keys)}`
                    );
                  }
                  onEventRef.current(payload);
                } catch (err) {
                  console.error(`${logPrefix} Parse error:`, err);
                }
              }
            }
          }
        }
      } catch (err) {
        setIsConnected(false);
        if ((err as Error).name !== 'AbortError') {
          console.error(`${logPrefix} Connection error:`, err);
          scheduleReconnect();
        }
      }
    }

    connectSSE();

    return () => {
      isCleaningUp = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      abortController.abort();
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { isConnected };
};
